import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  Activity,
  Calendar,
  Award,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

function formatPercent(value) {
  return `${value >= 0 ? '+' : ''}${value}%`;
}

// Simple bar chart component
function SimpleBarChart({ data, dataKey, labelKey, color = 'indigo' }) {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-slate-400 w-16 truncate">{item[labelKey]}</span>
          <div className="flex-1 h-6 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
              style={{ width: `${maxValue > 0 ? (item[dataKey] / maxValue) * 100 : 0}%` }}
            />
          </div>
          <span className="text-sm text-white w-20 text-right">
            {typeof item[dataKey] === 'number' && item[dataKey] > 100 
              ? formatCurrency(item[dataKey]) 
              : item[dataKey]}
          </span>
        </div>
      ))}
    </div>
  );
}

// Funnel visualization
function FunnelChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count || 0));
  
  return (
    <div className="space-y-2">
      {data.map((stage, i) => (
        <div key={stage.stage} className="relative">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 w-24 capitalize">{stage.stage}</span>
            <div 
              className="h-10 bg-indigo-500/20 border border-indigo-500/30 rounded flex items-center justify-between px-3 transition-all"
              style={{ 
                width: `${maxCount > 0 ? Math.max(20, (stage.count / maxCount) * 100) : 20}%`,
                marginLeft: `${i * 2}%`
              }}
            >
              <span className="text-sm text-white font-medium">{stage.count} deals</span>
              <span className="text-sm text-green-400">{formatCurrency(stage.total_value)}</span>
            </div>
          </div>
          {stage.conversion_rate !== undefined && i > 0 && (
            <div className="ml-28 text-xs text-slate-500">
              ↓ {stage.conversion_rate}% conversion
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function CRMReports() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('12m');
  const [performance, setPerformance] = useState(null);
  const [revenueTrends, setRevenueTrends] = useState(null);
  const [revenueByClient, setRevenueByClient] = useState(null);
  const [winLoss, setWinLoss] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [velocity, setVelocity] = useState(null);
  const [pipelinePerformance, setPipelinePerformance] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, [period]);

  async function loadData() {
    setLoading(true);
    try {
      const [perfRes, trendRes, clientRes, winLossRes, funnelRes, velocityRes, pipelineRes] = await Promise.all([
        fetch(`${API_URL}/api/crm/reports/performance/summary`, { credentials: 'include' }),
        fetch(`${API_URL}/api/crm/reports/revenue/trends?period=${period}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/crm/reports/revenue/by-client?limit=5`, { credentials: 'include' }),
        fetch(`${API_URL}/api/crm/reports/deals/win-loss?period=${period}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/crm/reports/deals/funnel`, { credentials: 'include' }),
        fetch(`${API_URL}/api/crm/reports/deals/velocity`, { credentials: 'include' }),
        fetch(`${API_URL}/api/crm/reports/pipeline/performance`, { credentials: 'include' })
      ]);

      if (perfRes.ok) setPerformance(await perfRes.json());
      if (trendRes.ok) setRevenueTrends(await trendRes.json());
      if (clientRes.ok) setRevenueByClient(await clientRes.json());
      if (winLossRes.ok) setWinLoss(await winLossRes.json());
      if (funnelRes.ok) setFunnel(await funnelRes.json());
      if (velocityRes.ok) setVelocity(await velocityRes.json());
      if (pipelineRes.ok) setPipelinePerformance(await pipelineRes.json());
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }

  async function exportReport(type) {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/reports/export/csv?report_type=${type}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clientkit_${type}_report.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Report exported!');
      } else {
        toast.error('Failed to export report');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-700 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="crm-reports">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-indigo-400" />
            CRM Reports & Analytics
          </h1>
          <p className="text-slate-400 mt-1">
            Insights into your sales performance and revenue
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="3m" className="text-white">3 Months</SelectItem>
              <SelectItem value="6m" className="text-white">6 Months</SelectItem>
              <SelectItem value="12m" className="text-white">12 Months</SelectItem>
              <SelectItem value="ytd" className="text-white">Year to Date</SelectItem>
            </SelectContent>
          </Select>

          {/* Export Menu */}
          <Select onValueChange={exportReport}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Export" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="revenue" className="text-white">Revenue CSV</SelectItem>
              <SelectItem value="deals" className="text-white">Deals CSV</SelectItem>
              <SelectItem value="clients" className="text-white">Clients CSV</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={loadData}
            className="border-slate-700 text-slate-300"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* This Month Revenue */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              {revenueTrends?.summary?.growth_rate_percent !== 0 && (
                <Badge 
                  variant="outline" 
                  className={revenueTrends?.summary?.growth_rate_percent >= 0 
                    ? "bg-green-500/20 text-green-300 border-green-500/30" 
                    : "bg-red-500/20 text-red-300 border-red-500/30"}
                >
                  {revenueTrends?.summary?.growth_rate_percent >= 0 ? (
                    <ArrowUpRight className="w-3 h-3 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 mr-1" />
                  )}
                  {formatPercent(revenueTrends?.summary?.growth_rate_percent || 0)}
                </Badge>
              )}
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(performance?.revenue?.this_month || 0)}
            </p>
            <p className="text-sm text-slate-400">Revenue this month</p>
          </CardContent>
        </Card>

        {/* YTD Revenue */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-indigo-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(performance?.revenue?.year_to_date || 0)}
            </p>
            <p className="text-sm text-slate-400">Year to date</p>
          </CardContent>
        </Card>

        {/* Win Rate */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">
              {winLoss?.summary?.win_rate_percent || 0}%
            </p>
            <p className="text-sm text-slate-400">Win rate ({period})</p>
          </CardContent>
        </Card>

        {/* Pipeline Value */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Activity className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(performance?.revenue?.pipeline_value || 0)}
            </p>
            <p className="text-sm text-slate-400">Pipeline value</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trends */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Revenue Trends
            </CardTitle>
            <CardDescription className="text-slate-400">
              Monthly revenue over {period}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {revenueTrends?.monthly_data && (
              <SimpleBarChart
                data={revenueTrends.monthly_data.slice(-12)}
                dataKey="revenue"
                labelKey="month"
                color="green"
              />
            )}
            <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400">Total Revenue</p>
                <p className="text-lg font-bold text-white">
                  {formatCurrency(revenueTrends?.summary?.total_revenue || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Avg Monthly</p>
                <p className="text-lg font-bold text-white">
                  {formatCurrency(revenueTrends?.summary?.average_monthly || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-400" />
              Top Clients by Revenue
            </CardTitle>
            <CardDescription className="text-slate-400">
              Your highest-value clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            {revenueByClient?.clients && (
              <SimpleBarChart
                data={revenueByClient.clients}
                dataKey="total_revenue"
                labelKey="client_name"
                color="indigo"
              />
            )}
            {(!revenueByClient?.clients || revenueByClient.clients.length === 0) && (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No client revenue data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deal Funnel */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-400" />
              Sales Funnel
            </CardTitle>
            <CardDescription className="text-slate-400">
              Deal progression through stages
            </CardDescription>
          </CardHeader>
          <CardContent>
            {funnel?.funnel && <FunnelChart data={funnel.funnel} />}
            <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400">Total Deals</p>
                <p className="text-lg font-bold text-white">{funnel?.summary?.total_deals || 0}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Pipeline</p>
                <p className="text-lg font-bold text-green-400">
                  {formatCurrency(funnel?.summary?.pipeline_value || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Won</p>
                <p className="text-lg font-bold text-indigo-400">
                  {formatCurrency(funnel?.summary?.won_value || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Win/Loss Analysis */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              Win/Loss Analysis
            </CardTitle>
            <CardDescription className="text-slate-400">
              Deal outcomes breakdown
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Win/Loss Summary */}
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-500/20 border-4 border-green-500 flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-green-400">{winLoss?.summary?.total_won || 0}</span>
                </div>
                <p className="text-sm text-slate-400">Won</p>
                <p className="text-xs text-green-400">{formatCurrency(winLoss?.summary?.won_value || 0)}</p>
              </div>
              <div className="text-4xl text-slate-600">vs</div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-red-500/20 border-4 border-red-500 flex items-center justify-center mb-2">
                  <span className="text-2xl font-bold text-red-400">{winLoss?.summary?.total_lost || 0}</span>
                </div>
                <p className="text-sm text-slate-400">Lost</p>
                <p className="text-xs text-red-400">{formatCurrency(winLoss?.summary?.lost_value || 0)}</p>
              </div>
            </div>

            {/* Win Rate by Deal Size */}
            <h4 className="text-sm font-semibold text-slate-300 mb-3">Win Rate by Deal Size</h4>
            <div className="space-y-2">
              {winLoss?.by_deal_size?.map((size) => (
                <div key={size.size} className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-20 capitalize">{size.size}</span>
                  <div className="flex-1 h-4 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                      style={{ width: `${size.win_rate}%` }}
                    />
                  </div>
                  <span className="text-sm text-white w-16 text-right">{size.win_rate}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deal Velocity */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-400" />
            Deal Velocity
          </CardTitle>
          <CardDescription className="text-slate-400">
            Average time deals spend in each stage
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {velocity?.stage_velocity?.map((stage) => (
              <div key={stage.stage} className="text-center p-4 bg-slate-900/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{stage.avg_days}</p>
                <p className="text-xs text-slate-400 mb-1">days</p>
                <p className="text-sm text-slate-300 capitalize">{stage.stage}</p>
                <p className="text-xs text-slate-500">{stage.deal_count} deals</p>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-300">Average Sales Cycle</p>
                <p className="text-xs text-slate-400">From lead to won</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-indigo-400">
                  {velocity?.summary?.avg_sales_cycle_days || 0}
                </p>
                <p className="text-xs text-slate-400">days</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Performance Analytics */}
      {pipelinePerformance && pipelinePerformance.pipelines?.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700" data-testid="pipeline-analytics">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              Pipeline Performance Comparison
            </CardTitle>
            <CardDescription className="text-slate-400">
              Conversion rates and metrics across your sales pipelines
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">
                  {pipelinePerformance.summary?.total_pipelines || 0}
                </p>
                <p className="text-xs text-slate-400">Active Pipelines</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">
                  {pipelinePerformance.summary?.total_deals_all_pipelines || 0}
                </p>
                <p className="text-xs text-slate-400">Total Deals</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-400">
                  {pipelinePerformance.summary?.overall_win_rate || 0}%
                </p>
                <p className="text-xs text-slate-400">Overall Win Rate</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-400">
                  {formatCurrency(pipelinePerformance.summary?.total_pipeline_value || 0)}
                </p>
                <p className="text-xs text-slate-400">Pipeline Value</p>
              </div>
            </div>

            {/* Best Performing Pipeline */}
            {pipelinePerformance.summary?.best_performing_pipeline && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Award className="w-6 h-6 text-green-400" />
                  <div>
                    <p className="text-sm text-green-300">Best Performing Pipeline</p>
                    <p className="text-lg font-semibold text-white">
                      {pipelinePerformance.summary.best_performing_pipeline.name}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-400">
                    {pipelinePerformance.summary.best_performing_pipeline.win_rate}%
                  </p>
                  <p className="text-xs text-slate-400">Win Rate</p>
                </div>
              </div>
            )}

            {/* Pipeline Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-400">Pipeline</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-400">Deals</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-400">Won</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-400">Win Rate</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-400">Avg Value</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-slate-400">Avg Cycle</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-400">Pipeline Value</th>
                  </tr>
                </thead>
                <tbody>
                  {pipelinePerformance.pipelines.map((pipeline) => (
                    <tr key={pipeline.pipeline_id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{pipeline.pipeline_name}</span>
                          {pipeline.is_default && (
                            <Badge variant="secondary" className="text-[10px] bg-purple-500/20 text-purple-300">
                              Default
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="text-center py-3 px-2 text-slate-300">
                        {pipeline.metrics.total_deals}
                      </td>
                      <td className="text-center py-3 px-2 text-green-400">
                        {pipeline.metrics.won_deals}
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className={`font-medium ${
                          pipeline.metrics.win_rate_percent >= 50 
                            ? 'text-green-400' 
                            : pipeline.metrics.win_rate_percent >= 30 
                              ? 'text-amber-400' 
                              : 'text-slate-400'
                        }`}>
                          {pipeline.metrics.win_rate_percent}%
                        </span>
                      </td>
                      <td className="text-center py-3 px-2 text-slate-300">
                        {formatCurrency(pipeline.metrics.avg_deal_value)}
                      </td>
                      <td className="text-center py-3 px-2 text-slate-300">
                        {pipeline.metrics.avg_sales_cycle_days > 0 
                          ? `${pipeline.metrics.avg_sales_cycle_days} days` 
                          : '-'}
                      </td>
                      <td className="text-right py-3 px-2 text-amber-400 font-medium">
                        {formatCurrency(pipeline.metrics.pipeline_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Win Rate Comparison Bar Chart */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Win Rate by Pipeline</h4>
              <div className="space-y-2">
                {pipelinePerformance.pipelines.map((pipeline) => (
                  <div key={pipeline.pipeline_id} className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-32 truncate">
                      {pipeline.pipeline_name}
                    </span>
                    <div className="flex-1 h-6 bg-slate-900 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          pipeline.metrics.win_rate_percent >= 50 
                            ? 'bg-green-500' 
                            : pipeline.metrics.win_rate_percent >= 30 
                              ? 'bg-amber-500' 
                              : 'bg-slate-600'
                        }`}
                        style={{ width: `${pipeline.metrics.win_rate_percent}%` }}
                      />
                    </div>
                    <span className="text-sm text-white w-12 text-right">
                      {pipeline.metrics.win_rate_percent}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
